import type { EnvironmentalYoyRow } from '@siliconx/shared';
import { formatMonthLabel, formatTonnes } from './format';

/**
 * SIL-487 — year-over-year comparison table. REAL `asset_use_phase_snapshot`
 * data (Scope 2, one row per computed month, most-recent-first); no sample
 * fallback (see `useYoyTable` / environmentalSampleData.ts). A month with no
 * same-calendar-month row exactly one year earlier renders an explicit
 * "n/a" in the Prior FY / Δ columns — never a fabricated zero or a dropped
 * row (resources/plans/sustainability-dashboard-spec.md §3).
 */
export function YoyTable({ rows }: { rows: EnvironmentalYoyRow[] }) {
  if (rows.length === 0) {
    return <p className="py-2 text-sm text-zinc-500">No use-phase history yet.</p>;
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-zinc-200 text-xs font-semibold uppercase tracking-widest text-zinc-500">
          <th className="pb-1.5 text-left font-semibold">Month</th>
          <th className="pb-1.5 text-right font-semibold">Prior FY</th>
          <th className="pb-1.5 text-right font-semibold">Current FY</th>
          <th className="pb-1.5 text-right font-semibold">Δ</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.periodKey} className="border-b border-zinc-100 last:border-0">
            <td className="py-1.5">{formatMonthLabel(r.periodKey)}</td>
            <td className="py-1.5 text-right font-mono tabular-nums text-zinc-500">
              {r.priorYearKgco2e === null ? 'n/a' : formatTonnes(r.priorYearKgco2e)}
            </td>
            <td className="py-1.5 text-right font-mono font-semibold tabular-nums">
              {formatTonnes(r.currentYearKgco2e)}
            </td>
            <td
              className={`py-1.5 text-right font-mono font-semibold tabular-nums ${
                r.deltaPct === null ? 'text-zinc-400' : r.deltaPct <= 0 ? 'text-emerald-600' : 'text-amber-600'
              }`}
            >
              {r.deltaPct === null ? 'n/a' : `${r.deltaPct.toFixed(1)}%`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
